import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface NeuButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  fullWidth?: boolean;
  children: ReactNode;
}

export const NeuButton = forwardRef<HTMLButtonElement, NeuButtonProps>(
  ({ variant = 'primary', size = 'md', loading, disabled, fullWidth, children, className, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        style={variant === 'primary' ? { background: 'var(--sime-gradient-1)', boxShadow: '0 4px 16px var(--sime-accent-glow)' } : undefined}
        className={cn(
          'inline-flex items-center justify-center font-semibold transition-all duration-200 rounded-xl min-h-[44px]',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sime-accent',
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
          fullWidth && 'w-full',
          size === 'sm' && 'px-4 py-2 text-sm',
          size === 'md' && 'px-6 py-3 text-base',
          size === 'lg' && 'px-8 py-4 text-lg',
          variant === 'primary' && 'text-white hover:translate-y-[-1px] active:translate-y-0 active:scale-[0.97]',
          variant === 'secondary' && 'bg-sime-surface text-sime-text-primary shadow-neu-btn hover:shadow-neu-btn-hover active:shadow-neu-btn-active active:scale-[0.97]',
          variant === 'danger' && 'bg-sime-danger text-white shadow-neu-btn hover:shadow-neu-btn-hover active:shadow-neu-btn-active active:scale-[0.97]',
          variant === 'ghost' &&
            'shadow-none hover:shadow-none bg-transparent text-sime-text-secondary hover:text-sime-accent',
          className,
        )}
        {...props}
      >
        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {children}
      </button>
    );
  },
);

NeuButton.displayName = 'NeuButton';
